import { useEffect, useMemo, useState } from "react";
import { Copy, Download, Pin, PinOff, Plus, Table, Trash2 } from "lucide-react";

import { WatchlistGrid, type WatchlistSortState } from "@/components/grid/WatchlistGrid";
import { SymbolSearchInput } from "@/components/symbol/SymbolSearchInput";
import { WatchlistCopyModal } from "@/components/watchlist/WatchlistCopyModal";
import { WatchlistExportModal } from "@/components/watchlist/WatchlistExportModal";
import { CompactIcon, ReadonlyIcon } from "@/components/watchlist/WatchlistModeIcons";
import { WATCHLIST_ROW_COLORS, getWatchlistRowColor } from "@/components/watchlist/watchlistColors";
import { formatNumber } from "@/lib/utils";
import type { Watchlist, WatchlistItem } from "@/services/api/types";

interface WatchlistWorkspaceProps {
  watchlists: Watchlist[];
  selectedWatchlistId: number | null;
  items: WatchlistItem[];
  activeSymbol: string;
  viewMode: "single" | "compact" | "table";
  viewOptions?: Array<"single" | "compact" | "table">;
  maxListsPerUser: number;
  loading?: boolean;
  sortState: WatchlistSortState;
  onSelectWatchlist: (watchlistId: number) => void;
  onSelectSymbol: (symbol: string) => void;
  onViewModeChange: (view: "single" | "compact" | "table") => void;
  onSortChange: (sort: WatchlistSortState) => void;
  onCreateWatchlist: (name: string) => Promise<void> | void;
  onCopyWatchlist: (watchlistId: number, name: string) => Promise<void> | void;
  onRenameWatchlist: (watchlistId: number, name: string) => Promise<void> | void;
  onDeleteWatchlist: (watchlistId: number) => Promise<void> | void;
  onAddSymbol: (symbol: string) => Promise<void> | void;
  onPinToggle: (item: WatchlistItem, pinned: boolean) => Promise<void> | void;
  onSetRowColor: (item: WatchlistItem, color: string) => Promise<void> | void;
  onRemoveItem: (item: WatchlistItem) => Promise<void> | void;
}

interface RowMenuState {
  x: number;
  y: number;
  item: WatchlistItem;
}

export function WatchlistWorkspace({
  watchlists,
  selectedWatchlistId,
  items,
  activeSymbol,
  viewMode,
  viewOptions,
  maxListsPerUser,
  loading = false,
  sortState,
  onSelectWatchlist,
  onSelectSymbol,
  onViewModeChange,
  onSortChange,
  onCreateWatchlist,
  onCopyWatchlist,
  onRenameWatchlist,
  onDeleteWatchlist,
  onAddSymbol,
  onPinToggle,
  onSetRowColor,
  onRemoveItem,
}: WatchlistWorkspaceProps) {
  const [addSymbolInput, setAddSymbolInput] = useState("");
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState<RowMenuState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selectedWatchlist = useMemo(
    () => watchlists.find((item) => item.id === selectedWatchlistId) || watchlists[0] || null,
    [watchlists, selectedWatchlistId],
  );
  const readOnlyMode = viewMode === "single";
  const resolvedViewOptions = useMemo<Array<"single" | "compact" | "table">>(() => {
    if (!viewOptions?.length) {
      return ["compact", "table"];
    }
    return viewOptions;
  }, [viewOptions]);
  const readOnlyViewOptions = useMemo<Array<"single" | "compact">>(() => {
    const options = resolvedViewOptions.filter((option) => option === "single" || option === "compact") as Array<"single" | "compact">;
    if (options.length) {
      return options;
    }
    return ["single", "compact"];
  }, [resolvedViewOptions]);
  const modeOptions = readOnlyMode ? readOnlyViewOptions : resolvedViewOptions;
  const canCreate = watchlists.length < maxListsPerUser;
  const modeSwitchWidth = modeOptionsWidth(modeOptions.length);

  useEffect(() => {
    if (readOnlyMode) {
      setRowMenu(null);
      setAddPickerOpen(false);
      setCopyDialogOpen(false);
      setExportDialogOpen(false);
    }
  }, [readOnlyMode]);

  async function runAction(action: () => Promise<void> | void): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "自选股操作失败");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleAddSymbol(rawSymbol?: string) {
    const symbol = (rawSymbol || addSymbolInput).trim().toUpperCase();
    if (!symbol) {
      return;
    }
    await runAction(async () => {
      await onAddSymbol(symbol);
      setAddSymbolInput("");
      setAddPickerOpen(false);
    });
  }

  function openRowMenu(event: MouseEvent, item: WatchlistItem) {
    if (readOnlyMode) {
      return;
    }
    event.preventDefault();
    setRowMenu({
      x: event.clientX,
      y: event.clientY,
      item,
    });
  }

  return (
    <section className="flex min-h-0 flex-col border-b border-[var(--border)]" onContextMenu={(event) => event.preventDefault()}>
      <div className="border-b border-[var(--border)] px-3 py-2">
        <div className="relative">
          <select
            value={selectedWatchlistId ?? selectedWatchlist?.id ?? ""}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (!Number.isFinite(next) || next <= 0) {
                return;
              }
              onSelectWatchlist(next);
            }}
            className="h-8 w-full min-w-0 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-200"
          >
            {watchlists.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center bg-gradient-to-l from-[#101318] via-[#101318] to-transparent pl-2">
            <div
              className="grid h-8 shrink-0 gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] p-1"
              style={{
                width: modeSwitchWidth,
                gridTemplateColumns: `repeat(${modeOptions.length}, minmax(0, 1fr))`,
              }}
            >
              {modeOptions.map((option) => {
                const label = option === "single" ? "ReadOnly" : option === "compact" ? "Compact" : "Table";
                return (
                  <button
                    key={option}
                    className={`inline-flex h-6 w-6 items-center justify-center rounded ${
                      viewMode === option ? "bg-[#2a2f37] text-white" : "text-[var(--muted)] hover:bg-white/5"
                    }`}
                    onClick={() => onViewModeChange(option)}
                    title={label}
                    aria-label={label}
                  >
                    {option === "single" ? (
                      <ReadonlyIcon className="size-3.5" />
                    ) : option === "compact" ? (
                      <CompactIcon className="size-3.5" />
                    ) : (
                      <Table className="size-3.5" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {!readOnlyMode ? (
          <>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                className="inline-flex h-8 items-center justify-center gap-1 rounded border border-[var(--border)] bg-[#111823] px-2 text-xs text-slate-100 disabled:opacity-50"
                onClick={() => {
                  const name = window.prompt("新建自选股名称");
                  if (!name?.trim()) {
                    return;
                  }
                  void runAction(() => onCreateWatchlist(name.trim()));
                }}
                disabled={!canCreate || busy}
                title={canCreate ? "新建自选股" : `最多 ${maxListsPerUser} 个自选股`}
              >
                <Plus className="size-3.5" />
                新建
              </button>
              <button
                className="inline-flex h-8 items-center justify-center gap-1 rounded border border-[var(--border)] bg-[#111823] px-2 text-xs text-slate-100 disabled:opacity-50"
                onClick={() => setExportDialogOpen(true)}
                disabled={!selectedWatchlist || !items.length || busy}
                title="导出 CSV"
              >
                <Download className="size-3.5" />
                导出
              </button>
              <button
                className="inline-flex h-8 items-center justify-center gap-1 rounded border border-[var(--border)] bg-[#111823] px-2 text-xs text-slate-100 disabled:opacity-50"
                onClick={() => setCopyDialogOpen(true)}
                disabled={!selectedWatchlist || busy}
                title="复制到新自选股"
              >
                <Copy className="size-3.5" />
                复制
              </button>
              <button
                className="h-8 rounded border border-[var(--border)] bg-[#111823] px-2 text-xs text-slate-100 disabled:opacity-50"
                onClick={() => {
                  if (!selectedWatchlist) {
                    return;
                  }
                  const name = window.prompt("重命名自选股", selectedWatchlist.name);
                  if (!name?.trim() || name.trim() === selectedWatchlist.name) {
                    return;
                  }
                  void runAction(() => onRenameWatchlist(selectedWatchlist.id, name.trim()));
                }}
                disabled={!selectedWatchlist || busy}
              >
                改名
              </button>
              <button
                className="inline-flex h-8 w-8 items-center justify-center rounded border border-[var(--border)] bg-[#111823] text-slate-100 disabled:opacity-50"
                onClick={() => {
                  if (!selectedWatchlist) {
                    return;
                  }
                  if (!window.confirm(`确认删除自选股「${selectedWatchlist.name}」？`)) {
                    return;
                  }
                  void runAction(() => onDeleteWatchlist(selectedWatchlist.id));
                }}
                disabled={!selectedWatchlist || busy}
                title="删除自选股"
              >
                <Trash2 className="size-3.5" />
              </button>
              <button
                className={`inline-flex h-8 w-8 items-center justify-center rounded border border-[var(--border)] text-slate-100 disabled:opacity-50 ${
                  addPickerOpen ? "bg-[#1a2637]" : "bg-[#111823] hover:bg-white/5"
                }`}
                onClick={() => setAddPickerOpen((prev) => !prev)}
                disabled={!selectedWatchlist || busy}
                title="添加标的"
              >
                <Plus className="size-3.5" />
              </button>
            </div>

            {addPickerOpen ? (
              <div className="mt-2 rounded border border-[var(--border)] bg-[#0c1016] p-2">
                <SymbolSearchInput
                  value={addSymbolInput}
                  onValueChange={setAddSymbolInput}
                  onSubmitSymbol={async (symbol) => {
                    await handleAddSymbol(symbol);
                  }}
                  placeholder="搜索并添加标的，如 600519.SH"
                  inputClassName="h-8 border-[var(--border)] bg-[#0a0d12] text-xs"
                  disabled={busy}
                  autoFocus
                />
              </div>
            ) : null}
          </>
        ) : null}

        {error ? <div className="mt-2 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1.5 text-xs text-rose-200">{error}</div> : null}
      </div>

      <div className="terminal-scrollbar min-h-0 flex-1 overflow-y-auto" onClick={() => setRowMenu(null)}>
        {loading ? (
          <div className="px-3 py-8 text-sm text-[var(--muted)]">加载中...</div>
        ) : viewMode === "table" ? (
          <WatchlistGrid
            rows={items}
            activeSymbol={activeSymbol}
            defaultSort={sortState}
            gridKey={String(selectedWatchlist?.id || "watchlist")}
            onSortChange={(nextSort) => {
              if (!nextSort) {
                return;
              }
              void runAction(() => onSortChange(nextSort));
            }}
            onSelectSymbol={onSelectSymbol}
            onRowContextMenu={(payload) => openRowMenu(payload.event, payload.row)}
          />
        ) : viewMode === "single" ? (
          <SingleWatchlistList
            rows={items}
            activeSymbol={activeSymbol}
            onSelectSymbol={onSelectSymbol}
            onContextMenu={openRowMenu}
          />
        ) : (
          <CompactWatchlistList
            rows={items}
            activeSymbol={activeSymbol}
            sortState={sortState}
            onSortChange={(nextSort) => {
              void runAction(() => onSortChange(nextSort));
            }}
            onSelectSymbol={onSelectSymbol}
            onContextMenu={openRowMenu}
          />
        )}
      </div>

      {!readOnlyMode && rowMenu ? (
        <div
          className="fixed z-40 w-[220px] rounded border border-[var(--border-strong)] bg-[#0f151f] p-2 shadow-2xl"
          style={{ left: Math.max(8, rowMenu.x), top: Math.max(8, rowMenu.y) }}
        >
          <button
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-100 hover:bg-white/5"
            onClick={() => {
              void runAction(() => onPinToggle(rowMenu.item, !Boolean(rowMenu.item.pinned)));
              setRowMenu(null);
            }}
          >
            {rowMenu.item.pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
            {rowMenu.item.pinned ? "解除置顶" : "永远置顶"}
          </button>
          <div className="mt-1 border-t border-[var(--border)] pt-1">
            <div className="px-2 pb-1 text-[10px] uppercase tracking-wide text-[var(--muted)]">行背景色</div>
            <div className="grid grid-cols-5 gap-1 px-1">
              {WATCHLIST_ROW_COLORS.map((color) => (
                <button
                  key={color.token}
                  title={color.label}
                  className="h-6 rounded border border-white/10"
                  style={{ backgroundColor: color.background }}
                  onClick={() => {
                    void runAction(() => onSetRowColor(rowMenu.item, color.token));
                    setRowMenu(null);
                  }}
                />
              ))}
              <button
                className="col-span-5 mt-1 h-6 rounded border border-[var(--border)] text-xs text-slate-200 hover:bg-white/5"
                onClick={() => {
                  void runAction(() => onSetRowColor(rowMenu.item, ""));
                  setRowMenu(null);
                }}
              >
                清除颜色
              </button>
            </div>
          </div>
          <button
            className="mt-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-rose-200 hover:bg-rose-500/15"
            onClick={() => {
              void runAction(() => onRemoveItem(rowMenu.item));
              setRowMenu(null);
            }}
          >
            <Trash2 className="size-3.5" />
            移除标的
          </button>
        </div>
      ) : null}

      {!readOnlyMode ? (
        <>
          <WatchlistExportModal
            open={exportDialogOpen}
            watchlistName={selectedWatchlist?.name || "watchlist"}
            items={items}
            onClose={() => setExportDialogOpen(false)}
          />
          <WatchlistCopyModal
            open={copyDialogOpen}
            sourceWatchlistName={selectedWatchlist?.name || ""}
            busy={busy}
            onClose={() => setCopyDialogOpen(false)}
            onSubmit={async (name) => {
              if (!selectedWatchlist) {
                return;
              }
              const success = await runAction(() => onCopyWatchlist(selectedWatchlist.id, name));
              if (success) {
                setCopyDialogOpen(false);
              }
            }}
          />
        </>
      ) : null}
    </section>
  );
}

function SingleWatchlistList({
  rows,
  activeSymbol,
  onSelectSymbol,
  onContextMenu,
}: {
  rows: WatchlistItem[];
  activeSymbol: string;
  onSelectSymbol: (symbol: string) => void;
  onContextMenu: (event: MouseEvent, item: WatchlistItem) => void;
}) {
  if (!rows.length) {
    return <div className="px-3 py-6 text-sm text-[var(--muted)]">当前自选股为空</div>;
  }
  return (
    <div className="px-2 py-1">
      {rows.map((item) => {
        const itemName = item.display_name || item.stock_name || item.name_cache || item.symbol;
        const active = item.symbol === activeSymbol;
        const color = getWatchlistRowColor(item.row_color);
        return (
          <button
            key={item.id}
            className={`mb-1.5 w-full rounded border border-[var(--border)] px-2 py-1.5 text-left text-xs transition ${
              active ? "ring-1 ring-sky-400/60" : "hover:bg-[#151920]"
            }`}
            style={color ? { backgroundColor: color.background, color: color.text } : undefined}
            onClick={() => onSelectSymbol(item.symbol)}
            onContextMenu={(event) => onContextMenu(event.nativeEvent, item)}
            title={item.symbol}
          >
            <span className="block truncate">{itemName}</span>
          </button>
        );
      })}
    </div>
  );
}

function CompactWatchlistList({
  rows,
  activeSymbol,
  sortState,
  onSortChange,
  onSelectSymbol,
  onContextMenu,
}: {
  rows: WatchlistItem[];
  activeSymbol: string;
  sortState: WatchlistSortState;
  onSortChange: (sort: WatchlistSortState) => void;
  onSelectSymbol: (symbol: string) => void;
  onContextMenu: (event: MouseEvent, item: WatchlistItem) => void;
}) {
  if (!rows.length) {
    return <div className="px-3 py-6 text-sm text-[var(--muted)]">当前自选股为空</div>;
  }
  return (
    <>
      <div className="grid grid-cols-[1.4fr_0.8fr_0.8fr] px-3 py-1 text-[10px] uppercase tracking-wide text-[var(--muted)]">
        <CompactSortHeader
          className="text-left"
          label="标的"
          sortField="name"
          sortState={sortState}
          onSortChange={onSortChange}
        />
        <CompactSortHeader
          className="text-right"
          label="最新价"
          sortField="close"
          sortState={sortState}
          onSortChange={onSortChange}
        />
        <CompactSortHeader
          className="text-right"
          label="涨跌%"
          sortField="pct_chg"
          sortState={sortState}
          onSortChange={onSortChange}
        />
      </div>
      {rows.map((item) => {
        const itemName = item.display_name || item.stock_name || item.name_cache || item.symbol;
        const active = item.symbol === activeSymbol;
        const positive = (item.pct_chg ?? 0) >= 0;
        const color = getWatchlistRowColor(item.row_color);
        return (
          <button
            key={item.id}
            className={`grid w-full grid-cols-[1.4fr_0.8fr_0.8fr] items-center gap-2 border-t border-[var(--border)] px-3 py-1.5 text-left text-xs transition ${
              active ? "ring-1 ring-sky-400/60" : "hover:bg-[#151920]"
            }`}
            style={color ? { backgroundColor: color.background, color: color.text } : undefined}
            onClick={() => onSelectSymbol(item.symbol)}
            onContextMenu={(event) => onContextMenu(event.nativeEvent, item)}
          >
            <div className="min-w-0">
              <div className="truncate">{itemName}</div>
              <div className="truncate text-[10px] opacity-80">{item.symbol}</div>
            </div>
            <div className="text-right">{formatNumber(item.close)}</div>
            <div className={`text-right ${positive ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
              {item.pct_chg != null ? `${item.pct_chg >= 0 ? "+" : ""}${formatNumber(item.pct_chg)}%` : "--"}
            </div>
          </button>
        );
      })}
    </>
  );
}

function CompactSortHeader({
  label,
  sortField,
  sortState,
  className,
  onSortChange,
}: {
  label: string;
  sortField: string;
  sortState: WatchlistSortState;
  className: string;
  onSortChange: (sort: WatchlistSortState) => void;
}) {
  const active = sortState.field === sortField;
  const indicator = active ? (sortState.direction === "asc" ? "↑" : "↓") : "";
  return (
    <button
      className={`min-w-0 truncate ${className} ${active ? "text-slate-200" : "text-[var(--muted)]"}`}
      onClick={() =>
        onSortChange({
          field: sortField,
          direction: nextCompactSortDirection(sortField, sortState),
        })
      }
    >
      {label} {indicator}
    </button>
  );
}

function nextCompactSortDirection(field: string, current: WatchlistSortState) {
  if (current.field === field) {
    return current.direction === "desc" ? "asc" : "desc";
  }
  if (field === "name" || field === "symbol" || field === "trade_date") {
    return "asc";
  }
  return "desc";
}

function modeOptionsWidth(count: number) {
  const size = Math.max(2, Math.min(3, count));
  if (size === 2) {
    return 72;
  }
  return 106;
}
